import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { authAPI, aiAPI } from '../services/api';
import toast from 'react-hot-toast';

// Initial state
const initialState = {
  user: null,
  loading: true,
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_ERROR: 'SET_ERROR',
  LOGOUT: 'LOGOUT',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        loading: false,
        error: null,
      };
    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        loading: false,
        error: null,
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authAPI.getMe();
      
      // Check different possible response structures
      const user = response.data.user || response.data.data?.user || response.data;
      
      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: user,
      });
    } catch (error) {
      // Only clear user if we get a 401 (unauthorized)
      // Other errors (network, 500, etc.) shouldn't clear the user
      if (error.response?.status === 401) {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      } else {
        dispatch({
          type: AUTH_ACTIONS.SET_LOADING,
          payload: false,
        });
      }
    }
  };

  const login = async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      const response = await authAPI.login(email, password);
      
      // Check different possible response structures
      const user = response.data.user || response.data.data?.user || response.data;
      
      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: user,
      });
      
      // Refresh auth state to ensure consistency
      setTimeout(() => checkAuth(), 100);
      
      toast.success('Welcome back!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: message,
      });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const signup = async (name, email, password, passwordConfirm) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      const response = await authAPI.signup(name, email, password, passwordConfirm);
      
      // Check different possible response structures
      const user = response.data.user || response.data.data?.user || response.data;
      
      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: user,
      });
      
      // Refresh auth state to ensure consistency
      setTimeout(() => checkAuth(), 100);
      
      toast.success('Account created successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Signup failed';
      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: message,
      });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const googleAuth = async (credential) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      const response = await authAPI.googleAuth(credential);
      
      const user = response.data.user || response.data.data?.user || response.data;
      
      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: user,
      });
      
      setTimeout(() => checkAuth(), 100);
      
      toast.success('Welcome!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Google authentication failed';
      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: message,
      });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      toast.success('Logged out successfully');
    } catch (error) {
      // Even if logout fails on server, clear local state
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      toast.success('Logged out');
    }
  };

  const updateProfile = async (data) => {
    try {
      const response = await authAPI.updateMe(data);
      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: response.data.user,
      });
      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updatePassword = async (passwordCurrent, password, passwordConfirm) => {
    try {
      const response = await authAPI.updatePassword(passwordCurrent, password, passwordConfirm);
      
      // Update user state if returned
      const user = response.data?.data?.user;
      if (user) {
        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: user,
        });
      }
      
      toast.success('Password updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const setPassword = async (password, passwordConfirm) => {
    try {
      const response = await authAPI.setPassword(password, passwordConfirm);
      
      // Update user state to reflect hasPassword: true
      const user = response.data?.data?.user;
      if (user) {
        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: user,
        });
      }
      
      toast.success('Password set successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to set password';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verifyEmail = async (token) => {
    try {
      const response = await authAPI.verifyEmail(token);
      
      // Update user state to reflect verified email
      if (state.user) {
        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: { ...state.user, emailVerified: true },
        });
      } else {
        // If no user in state, just set loading to false
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
      
      toast.success('Email verified successfully!');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Email verification failed';
      // Don't dispatch error state - just return the error
      // This prevents re-renders that could cause loops
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const resendVerificationEmail = async () => {
    try {
      await authAPI.resendVerificationEmail();
      toast.success('Verification email sent! Please check your inbox.');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send verification email';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const checkVerificationStatus = async () => {
    try {
      const response = await authAPI.checkVerificationStatus();
      return { 
        success: true, 
        emailVerified: response.data?.data?.emailVerified || false 
      };
    } catch (error) {
      return { success: false, emailVerified: false };
    }
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Update the local user credits (called after a generation returns creditsRemaining)
  const updateCredits = useCallback((newCredits) => {
    if (state.user && typeof newCredits === 'number') {
      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: { ...state.user, credits: newCredits },
      });
    }
  }, [state.user]);

  // Fetch fresh credits and plan from the server
  const refreshCredits = useCallback(async () => {
    try {
      const res = await aiAPI.getCredits();
      const { credits, plan } = res.data?.data || {};
      if (state.user && typeof credits === 'number') {
        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: { ...state.user, credits, ...(plan && { plan }) },
        });
      }
      return credits;
    } catch {
      return null;
    }
  }, [state.user]);

  const value = {
    user: state.user,
    loading: state.loading,
    error: state.error,
    login,
    signup,
    googleAuth,
    logout,
    updateProfile,
    updatePassword,
    setPassword,
    verifyEmail,
    resendVerificationEmail,
    checkVerificationStatus,
    clearError,
    updateCredits,
    refreshCredits,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};